import React, { useState } from "react";
import { Card, Spin, Row, Col, Statistic, Divider, Button } from "antd";
import { LoadingOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import CountUp from "react-countup";
import { useNavigate } from "react-router-dom";

interface TurnoverProps {
  data: {
    turnover: number;
    totalProduceUSD: number;
    totalMaterialsUSD: number;
    totalDebtUSD: number;
    totalCompanyDebtUSD: number;
    totalClientPrepaidUSD: number;
    totalCompanyPrepaidUSD: number;
    totalCashboxBalanceUSD: number;
    totalCashTransferBalanceUSD: number;
    clientStatsByType: {
      [key: string]: {
        totalDebtUSD: number;
        totalCompanyDebtUSD: number;
        totalClientPrepaidAmountUSD: number;
        totalCompanyPrepaidAmountUSD: number;
        count: number;
      };
    };
  } | null;
  loading: boolean;
}

export default function Turnover({ data, loading }: TurnoverProps) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  
  if (loading) {
    return (
      <Card bordered={false} style={{ width: "100%" }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card bordered={false} style={{ width: "100%" }}>
        <div>No data available</div>
      </Card>
    );
  }

  return (
    <Card 
      bordered={false} 
      style={{ width: "100%" }} 
      onClick={() => navigate("/turnover/logs")} 
      hoverable
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Общий оборот</h3>
          <CountUp
            start={0}
            end={data.turnover}
            duration={2.5}
            decimals={2}
            prefix="$"
            separator=" "
            style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}
          />
        </div>
      }
    >
      {/* Общая разбивка цифр оборота */}
      {showDetails && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Продукция"
                value={data.totalProduceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Материалы"
                value={data.totalMaterialsUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Долг клиентов"
                value={data.totalDebtUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Долг компании"
                value={data.totalCompanyDebtUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Баланс касс"
                value={data.totalCashboxBalanceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Конвертация"
                value={data.totalCashTransferBalanceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>
          
          <Divider />
        </>
      )}
      
      {/* Кнопка для показа детальной разбивки */}
      {data.clientStatsByType && Object.keys(data.clientStatsByType).length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Button 
            type="link" 
            onClick={(e) => {
              e.stopPropagation(); // Предотвращаем переход на другую страницу
              setShowDetails(!showDetails);
            }}
            icon={showDetails ? <UpOutlined /> : <DownOutlined />}
            style={{ color: '#1890ff' }}
          >
            {showDetails ? 'Скрыть детали' : 'Показать подробную разбивку оборота'}
          </Button>
        </div>
      )}
      
      {/* Детальная разбивка по типам клиентов */}
      {showDetails && data.clientStatsByType && Object.keys(data.clientStatsByType).length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#1890ff' }}>Детальная разбивка по типам клиентов:</h4>
            <Row gutter={[16, 16]}>
              {Object.entries(data.clientStatsByType).map(([clientType, stats]) => (
                <Col xs={24} sm={12} md={8} key={clientType}>
                  <Card 
                    size="small" 
                    title={
                      <span style={{ 
                        color: clientType === 'purchase' ? '#52c41a' : 
                               clientType === 'sale' ? '#1890ff' : '#fa8c16',
                        fontWeight: 'bold'
                      }}>
                        {clientType === 'purchase' ? 'Покупки' : 
                         clientType === 'sale' ? 'Продажи' : 
                         clientType === 'scrap' ? 'Лом' : clientType}
                        <span style={{ fontSize: '12px', marginLeft: '8px', color: '#666' }}>
                          ({stats.count} клиентов)
                        </span>
                      </span>
                    }
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Долг клиентов:</span>
                      <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                        ${stats.totalDebtUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Долг компании:</span>
                      <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ${stats.totalCompanyDebtUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Предоплата клиентов:</span>
                      <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ${stats.totalClientPrepaidAmountUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Предоплата компании:</span>
                      <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                        ${stats.totalCompanyPrepaidAmountUSD}
                      </span>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          <Divider />
        </>
      )}
      
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <small style={{ color: '#666' }}>
          Формула: Продукция + Материалы + Долг клиентов - Долг компании - Предоплата клиентов + Предоплата компании + Баланс касс + Конвертация
        </small>
      </div>
    </Card>
  );
}
